import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import Potrace from "potrace";
import { finishApplyLoading } from "./stitchApplyLoading";
import { collectLayerPathDs } from "./stitchPotraceBitmap";

const FILL_SCALE = 2; // canvas bitmap pixels per CSS pixel

/**
 * Map Potrace bitmap pixel coords back to SVG viewBox coordinates.
 * Mirror of the same helper in StitchEraseCanvas.jsx.
 */
function scaleAndOffsetPath(d, scale, offsetX, offsetY) {
  if (!d) return d;
  let coordIdx = 0;
  return d.replace(
    /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
    (n) => {
      const isX = coordIdx % 2 === 0;
      coordIdx++;
      const scaled = parseFloat(n) * scale;
      return (scaled + (isX ? offsetX : offsetY)).toFixed(2);
    },
  );
}

/** Parse a CSS hex color (#rrggbb or #rgb) into { r, g, b }. */
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  if (h.length === 3) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/**
 * Paint-bucket overlay for the Stitch Editor.
 * Clicks flood-fill regions onto the bitmap and accumulate; changes are only
 * committed when the user presses Apply or when the tool is switched away.
 */
const StitchFillCanvas = forwardRef(({
  svgJoined,
  svgSplit,
  layerIdx,
  colors,
  layoutMetrics,
  pan,
  zoom,
  onApply,
}, ref) => {
  const canvasRef = useRef(null);
  // Separate overlay canvas for the live hover preview so it never touches the
  // committed bitmap. Sits directly on top of the base canvas.
  const hoverCanvasRef = useRef(null);
  // Cached mask of the region currently previewed — lets us skip the (costly)
  // flood-fill recompute while the pointer stays inside the same region.
  const lastRegionRef = useRef(null);
  const hoverRafRef = useRef(0);
  const pendingHoverRef = useRef(null);
  const handleApplyRef = useRef(null);
  const applyStartRef = useRef(0);
  const applyEndTimerRef = useRef(null);
  const dirtyRef = useRef(false);
  const applyExitResolverRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [dirty, setDirty] = useState(false);
  // The toolbar's controls slot mounts in the same commit as this overlay, so it
  // isn't in the DOM during our first render. Resolve it after mount (retrying a
  // frame in case the toolbar commits slightly later) so the Discard/Apply
  // controls and hint appear immediately on activation.
  const [controlsSlot, setControlsSlot] = useState(null);
  useEffect(() => {
    let raf;
    const find = () => {
      const el = document.getElementById("stitch-erase-controls-slot");
      if (el) { setControlsSlot(el); return; }
      raf = requestAnimationFrame(find);
    };
    find();
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, []);

  const { dw, dh, vbX, vbY, vbW, vbH, designLeft, designTop } = layoutMetrics;

  const W = Math.round(dw * FILL_SCALE);
  const H = Math.round(dh * FILL_SCALE);

  const fillColor = colors?.[layerIdx] || "#000000";

  const rasterizeLayer = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const source = svgSplit || svgJoined;
    if (!source?.children?.[layerIdx]) return;

    const layer = source.children[layerIdx];
    const viewBox = source.attributes?.viewBox || "0 0 1000 1000";

    const dStrings = collectLayerPathDs(layer);
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    if (dStrings.length === 0) return;

    const fillRule = layer?.attributes?.["fill-rule"] || layer?.attributes?.fillRule || "evenodd";
    const compoundD = dStrings.join(" ");
    const svgStr =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${W}" height="${H}">` +
      `<path d="${compoundD}" fill="${fillColor}" fill-rule="${fillRule}" />` +
      `</svg>`;

    const img = new Image();
    img.onload = () => { ctx.drawImage(img, 0, 0, W, H); };
    img.onerror = () => {
      const [vbx, vby, vbw, vbh] = viewBox.split(/\s+/).map(Number);
      ctx.save();
      ctx.translate(-vbx * (W / vbw), -vby * (H / vbh));
      ctx.scale(W / vbw, H / vbh);
      ctx.fillStyle = fillColor;
      try {
        const combinedPath = new Path2D(compoundD);
        ctx.fill(combinedPath, fillRule);
      } catch (_) { /* skip on unsupported path */ }
      ctx.restore();
    };

    try {
      const encoded = btoa(unescape(encodeURIComponent(svgStr)));
      img.src = "data:image/svg+xml;base64," + encoded;
    } catch (_) {
      img.onerror();
    }
  }, [svgJoined, svgSplit, layerIdx, fillColor, W, H]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = W;
    canvas.height = H;
    const hover = hoverCanvasRef.current;
    if (hover) {
      hover.width = W;
      hover.height = H;
      hover.getContext("2d")?.clearRect(0, 0, W, H);
    }
    lastRegionRef.current = null;
    setApplyError(null);
    setDirty(false);
    dirtyRef.current = false;
    rasterizeLayer();
  }, [W, H, rasterizeLayer]);

  useEffect(() => () => {
    if (applyEndTimerRef.current) clearTimeout(applyEndTimerRef.current);
    if (hoverRafRef.current) cancelAnimationFrame(hoverRafRef.current);
  }, []);

  const paintBucketFill = useCallback(
    (canvasX, canvasY) => {
      const canvas = canvasRef.current;
      if (!canvas) return false;

      const cx = Math.round(canvasX);
      const cy = Math.round(canvasY);
      const ctx = canvas.getContext("2d");
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const width = canvas.width;
      const height = canvas.height;

      if (cx < 0 || cx >= width || cy < 0 || cy >= height) return false;

      const idx = (cy * width + cx) * 4;
      const targetR = data[idx];
      const targetG = data[idx + 1];
      const targetB = data[idx + 2];
      const targetA = data[idx + 3];

      const { r: fillR, g: fillG, b: fillB } = hexToRgb(fillColor);

      if (targetR === fillR && targetG === fillG && targetB === fillB && targetA === 255) return false;

      const tolerance = (30 / 100) * 255 * 3;
      let changed = false;

      const visited = new Uint8Array(width * height);
      const stack = [cx + cy * width];

      while (stack.length > 0) {
        const key = stack.pop();
        if (visited[key]) continue;

        const x = key % width;
        const y = (key - x) / width;
        if (x < 0 || x >= width || y < 0 || y >= height) continue;

        const i = key * 4;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        const colorDist = Math.abs(r - targetR) + Math.abs(g - targetG) + Math.abs(b - targetB);
        if (colorDist <= tolerance && Math.abs(a - targetA) < 50) {
          visited[key] = 1;
          data[i] = fillR;
          data[i + 1] = fillG;
          data[i + 2] = fillB;
          data[i + 3] = 255;
          changed = true;

          if (x > 0) stack.push(key - 1);
          if (x < width - 1) stack.push(key + 1);
          if (y > 0) stack.push(key - width);
          if (y < height - 1) stack.push(key + width);
        }
      }

      if (changed) ctx.putImageData(imageData, 0, 0);
      return changed;
    },
    [fillColor],
  );

  const clearHover = useCallback(() => {
    lastRegionRef.current = null;
    const hover = hoverCanvasRef.current;
    if (hover) hover.getContext("2d")?.clearRect(0, 0, hover.width, hover.height);
  }, []);

  // Non-destructive flood-fill that highlights the region a click *would* fill,
  // giving the user a precise preview before committing. The fill color is
  // black-on-white in the raster, so we paint the preview onto a separate
  // overlay canvas in the tool's orange accent (soft interior + crisp outline).
  const computeHoverPreview = useCallback(
    (bmpX, bmpY) => {
      const canvas = canvasRef.current;
      const hover = hoverCanvasRef.current;
      if (!canvas || !hover) return;

      const width = canvas.width;
      const height = canvas.height;
      const cx = Math.round(bmpX);
      const cy = Math.round(bmpY);
      if (cx < 0 || cx >= width || cy < 0 || cy >= height) { clearHover(); return; }

      const startKey = cy * width + cx;
      // Still inside the region we already drew → nothing to do.
      if (lastRegionRef.current && lastRegionRef.current[startKey]) return;

      const ctx = canvas.getContext("2d");
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;

      const sIdx = startKey * 4;
      const targetR = data[sIdx];
      const targetG = data[sIdx + 1];
      const targetB = data[sIdx + 2];
      const targetA = data[sIdx + 3];

      const { r: fillR, g: fillG, b: fillB } = hexToRgb(fillColor);
      const alreadyFilled =
        targetR === fillR && targetG === fillG && targetB === fillB && targetA === 255;

      const tolerance = (30 / 100) * 255 * 3;
      const mask = new Uint8Array(width * height);
      const stack = [startKey];
      let count = 0;

      while (stack.length > 0) {
        const key = stack.pop();
        if (mask[key]) continue;
        const x = key % width;
        const y = (key - x) / width;
        const i = key * 4;
        const colorDist =
          Math.abs(data[i] - targetR) +
          Math.abs(data[i + 1] - targetG) +
          Math.abs(data[i + 2] - targetB);
        if (colorDist <= tolerance && Math.abs(data[i + 3] - targetA) < 50) {
          mask[key] = 1;
          count++;
          if (x > 0) stack.push(key - 1);
          if (x < width - 1) stack.push(key + 1);
          if (y > 0) stack.push(key - width);
          if (y < height - 1) stack.push(key + width);
        }
      }

      lastRegionRef.current = mask;

      const hctx = hover.getContext("2d");
      hctx.clearRect(0, 0, width, height);
      // Hovering over an area that's already this layer's color (or nothing to
      // fill) → show no preview so the user knows a click won't do anything.
      if (alreadyFilled || count === 0) return;

      const out = hctx.createImageData(width, height);
      const od = out.data;
      const aR = 234, aG = 88, aB = 12; // tool accent (#ea580c)
      for (let key = 0; key < mask.length; key++) {
        if (!mask[key]) continue;
        const x = key % width;
        const y = (key - x) / width;
        const boundary =
          x === 0 || x === width - 1 || y === 0 || y === height - 1 ||
          !mask[key - 1] || !mask[key + 1] ||
          !mask[key - width] || !mask[key + width];
        const i = key * 4;
        od[i] = aR;
        od[i + 1] = aG;
        od[i + 2] = aB;
        od[i + 3] = boundary ? 240 : 80;
      }
      hctx.putImageData(out, 0, 0);
    },
    [fillColor, clearHover],
  );

  const handleApply = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;
    applyStartRef.current = Date.now();
    setIsProcessing(true);
    setApplyError(null);

    let bwCanvas;
    try {
      const ctx = canvas.getContext("2d");
      const imageData = ctx.getImageData(0, 0, W, H);

      bwCanvas = document.createElement("canvas");
      bwCanvas.width = W;
      bwCanvas.height = H;
      const bwCtx = bwCanvas.getContext("2d");
      bwCtx.fillStyle = "#ffffff";
      bwCtx.fillRect(0, 0, W, H);
      const bwData = bwCtx.getImageData(0, 0, W, H);
      for (let i = 0; i < imageData.data.length; i += 4) {
        if (imageData.data[i + 3] > 10) {
          bwData.data[i] = bwData.data[i + 1] = bwData.data[i + 2] = 0;
          bwData.data[i + 3] = 255;
        }
      }
      bwCtx.putImageData(bwData, 0, 0);
    } catch (err) {
      console.error("[StitchFillCanvas] Canvas read error:", err);
      setIsProcessing(false);
      setApplyError("Could not read canvas. Try again.");
      applyExitResolverRef.current?.(false);
      applyExitResolverRef.current = null;
      return;
    }

    bwCanvas.toBlob((blob) => {
      if (!blob) {
        setIsProcessing(false);
        setApplyError("Failed to encode canvas. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        setIsProcessing(false);
        setApplyError("Vectorization timed out. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
      }, 15000);

      const trace = new Potrace.Potrace();
      trace.setParameters({ turdSize: 2, optTolerance: 0.2, alphaMax: 1, threshold: 128 });

      trace.loadImage(blobUrl, (loadErr) => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(blobUrl);
        if (loadErr) {
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
          return;
        }
        try {
          const pathTag = trace.getPathTag();
          const match = pathTag?.match(/d="([^"]+)"/);
          if (!match) {
            finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
              dirtyRef.current = false;
              applyExitResolverRef.current?.(true);
              applyExitResolverRef.current = null;
            });
            return;
          }
          const newPathD = scaleAndOffsetPath(match[1], vbW / W, vbX, vbY);
          finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
            dirtyRef.current = false;
            onApply?.(layerIdx, newPathD);
            applyExitResolverRef.current?.(true);
            applyExitResolverRef.current = null;
          });
        } catch (err) {
          console.error("[StitchFillCanvas] Potrace error:", err);
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
        }
      });
    }, "image/png");
  }, [W, H, vbX, vbY, vbW, layerIdx, onApply]);

  useEffect(() => { handleApplyRef.current = handleApply; }, [handleApply]);

  // Revert the bitmap back to the committed layer, dropping pending fills.
  const handleDiscard = useCallback(() => {
    if (isProcessing) return;
    setDirty(false);
    setApplyError(null);
    dirtyRef.current = false;
    rasterizeLayer();
  }, [isProcessing, rasterizeLayer]);

  useImperativeHandle(ref, () => ({
    hasWorkToApply: () => dirtyRef.current,
    applyAndExit: () => new Promise((resolve) => {
      if (isProcessing || !dirtyRef.current) {
        resolve(false);
        return;
      }
      applyExitResolverRef.current = resolve;
      handleApply();
    }),
  }), [isProcessing, handleApply]);

  const onPointerDown = useCallback(
    (e) => {
      if (e.button !== 0 || isProcessing) return;
      e.stopPropagation();

      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const cssX = ((e.clientX - rect.left) / rect.width) * dw;
      const cssY = ((e.clientY - rect.top) / rect.height) * dh;
      const bmpX = cssX * FILL_SCALE;
      const bmpY = cssY * FILL_SCALE;

      const changed = paintBucketFill(bmpX, bmpY);
      if (changed) {
        setApplyError(null);
        dirtyRef.current = true;
        setDirty(true);
        // The just-filled region is now this layer's color; drop the cached
        // preview and refresh so the highlight clears under the cursor.
        lastRegionRef.current = null;
        computeHoverPreview(bmpX, bmpY);
      }
    },
    [paintBucketFill, isProcessing, dw, dh, computeHoverPreview],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (isProcessing) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const bmpX = ((e.clientX - rect.left) / rect.width) * dw * FILL_SCALE;
      const bmpY = ((e.clientY - rect.top) / rect.height) * dh * FILL_SCALE;
      pendingHoverRef.current = { bmpX, bmpY };
      if (!hoverRafRef.current) {
        hoverRafRef.current = requestAnimationFrame(() => {
          hoverRafRef.current = 0;
          const p = pendingHoverRef.current;
          if (p) computeHoverPreview(p.bmpX, p.bmpY);
        });
      }
    },
    [isProcessing, dw, dh, computeHoverPreview],
  );

  const onPointerLeave = useCallback(() => {
    if (hoverRafRef.current) {
      cancelAnimationFrame(hoverRafRef.current);
      hoverRafRef.current = 0;
    }
    pendingHoverRef.current = null;
    clearHover();
  }, [clearHover]);

  // Hide the hover preview while a vectorization pass is running.
  useEffect(() => {
    if (isProcessing) clearHover();
  }, [isProcessing, clearHover]);

  const hint = applyError
    || (isProcessing
      ? "Applying fill…"
      : dirty
        ? "Click more highlighted areas, then Apply — or Discard"
        : "Hover to preview, then click to fill the highlighted area. Use the color picker below to recolor the whole layer.");

  return (
    <>
      <style>{`@keyframes stitch-ai-spin { to { transform: rotate(360deg); } }`}</style>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${dw}px`,
          height: `${dh}px`,
          transformOrigin: "0 0",
          transform: `translate(${pan.x + designLeft * zoom}px, ${pan.y + designTop * zoom}px) scale(${zoom})`,
          cursor: isProcessing ? "wait" : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='26' height='26' viewBox='0 0 26 26'%3E%3Cline x1='2' y1='13' x2='24' y2='13' stroke='%23fff' stroke-width='3.5' stroke-linecap='round'/%3E%3Cline x1='13' y1='2' x2='13' y2='24' stroke='%23fff' stroke-width='3.5' stroke-linecap='round'/%3E%3Cline x1='2.5' y1='13' x2='23.5' y2='13' stroke='%23111' stroke-width='1.3' stroke-linecap='round'/%3E%3Cline x1='13' y1='2.5' x2='13' y2='23.5' stroke='%23111' stroke-width='1.3' stroke-linecap='round'/%3E%3Ccircle cx='13' cy='13' r='2.4' fill='%23ea580c' stroke='%23fff' stroke-width='1.4'/%3E%3C/svg%3E") 13 13, crosshair`,
          zIndex: 50,
          touchAction: "none",
          userSelect: "none",
          pointerEvents: isProcessing ? "none" : "auto",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block" }}
        />
        <canvas
          ref={hoverCanvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "block",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        {isProcessing && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              background: "transparent",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                border: "3px solid rgba(234,88,12,0.2)",
                borderTop: "3px solid #ea580c",
                animation: "stitch-ai-spin 0.7s linear infinite",
                filter: "drop-shadow(0 1px 2px rgba(255,255,255,0.85))",
              }}
            />
            <span style={{
              fontSize: "11px",
              fontWeight: 600,
              color: "#ea580c",
              fontFamily: "Clash Grotesk, sans-serif",
              textShadow: "0 1px 3px rgba(255,255,255,0.95), 0 0 6px rgba(255,255,255,0.75)",
            }}>
              Applying fill…
            </span>
          </div>
        )}
      </div>

      {controlsSlot && createPortal(
          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "10px" }}>
            {isProcessing ? (
              <span style={{
                fontSize: "11px",
                color: "#ea580c",
                fontFamily: "Clash Grotesk, sans-serif",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <span style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  border: "2px solid rgba(234,88,12,0.25)",
                  borderTop: "2px solid #ea580c",
                  animation: "stitch-ai-spin 0.7s linear infinite",
                  display: "inline-block",
                  flexShrink: 0,
                }} />
                Applying…
              </span>
            ) : (
              <span style={{
                fontSize: "11px",
                fontWeight: 500,
                color: applyError ? "#ef4444" : "#ea580c",
                fontFamily: "Clash Grotesk, sans-serif",
                lineHeight: 1.35,
              }}>
                {hint}
              </span>
            )}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={handleDiscard}
                disabled={isProcessing || !dirty}
                style={{
                  flex: 1,
                  border: "1px solid rgba(0,0,0,0.1)",
                  background: "#fff",
                  color: "#888",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "7px 12px",
                  borderRadius: "8px",
                  cursor: (isProcessing || !dirty) ? "not-allowed" : "pointer",
                  fontFamily: "Clash Grotesk, sans-serif",
                  opacity: (isProcessing || !dirty) ? 0.5 : 1,
                }}
              >
                Discard
              </button>
              <button
                onClick={() => handleApplyRef.current?.()}
                disabled={isProcessing || !dirty}
                style={{
                  flex: 1,
                  border: "none",
                  background: (isProcessing || !dirty) ? "#f3f4f6" : "linear-gradient(135deg, #ea580c, #c2410c)",
                  color: (isProcessing || !dirty) ? "#9ca3af" : "#fff",
                  fontSize: "11px",
                  fontWeight: 700,
                  padding: "7px 12px",
                  borderRadius: "8px",
                  cursor: (isProcessing || !dirty) ? "not-allowed" : "pointer",
                  fontFamily: "Clash Grotesk, sans-serif",
                }}
              >
                Apply
              </button>
            </div>
          </div>,
          controlsSlot,
        )}
    </>
  );
});

export default StitchFillCanvas;
